import { defineStore } from "pinia";
import {
  HabilitationsApi,
  UserDetailApi,
  UsersHabilitationsImportApi,
  type BatchUpdateUserHabilitationsResponse,
  type BatchUpdateUsersHabilitationsDto,
  type FindAvailableHabilitationsRequest,
  type UpdateBookingHabilitationDto,
  type UpdateBookingHabilitationResponse,
  type UpdateSessionHabilitationDto,
  type UpdateSessionHabilitationsResponse,
  type UserHabilitationCertificateModel,
  type UsersHabilitationsImportResultResponse,
  type UsersHabilitationsResponseModel,
} from "@simbelapp/order-sdk";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { IHabilitation, IUsersHabilitationsStore } from "~/utils/interfaces/habilitations-interfaces";
import { useUsersHabilitationsValidation } from "~/composables/users-habilitations/useUsersHabilitations";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { usePagination } from "~/composables/paginations/usePagination";
import { useFilters } from "~/composables/filters/useFilters";

// Monotonic id so only the most recent fetchAllUserHabilitations request may write the list.
let latestHabilitationsRequestId = 0;

export const useUsersHabilitationsStore = defineStore("usersHabilitations", {
  state: (): IUsersHabilitationsStore => {
    return {
      usersHabilitations: null,
      userHabilitationDetails: null,
      habilitationsList: null,
      userHabilitationsList: null,
      showUserHabilitationDetailModal: false,
      showCsvImportModal: false,
      userError: null,
      habilitationError: null,
      durationYearsError: null,
      selectedUsersIdList: [],
      excludedUsersIdList: [],
      habilitationLoading: false,
      habilitationEmployee: [],
      offset: 0,
      maxOffset: 0,
    };
  },
  getters: {},
  actions: {
    /** CSV import. The SDK builds the multipart body, so the raw file goes in as-is. */
    async importUsersHabilitationsCsv(file: Blob): Promise<UsersHabilitationsImportResultResponse | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UsersHabilitationsImportApi);
        return await apiInstance.uploadUsersHabilitationsImport({ file });
      } catch {
        return null;
      }
    },

    async fetchAvailableHabilitations(params?: FindAvailableHabilitationsRequest) {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
        const response = await apiInstance.findAvailableHabilitations({
          lazyLoad: true,
          ...params,
        });

        this.habilitationsList = (response.data ?? []) as IHabilitation[];
        return response;
      } catch {
        feedback.error("Erreur", "small");
        this.habilitationsList = [];
        return { data: [], meta: { total: 0 } };
      }
    },

    async fetchUsersHabilitations() {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
      const pagination = usePagination();
      const filters = useFilters();

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
      if (!paginateOptions.sortingColumn) {
        paginateOptions.sortingColumn = "default";
      }

      const paginateQuery = new URLSearchParams(paginateOptions);
      const transformQuery = Object.fromEntries(paginateQuery.entries());
      const filtersQuery = filters.getFiltersForSDK();
      try {
        const response = await apiInstance.getUsersHabilitations({
          ...transformQuery,
          ...filtersQuery,
        });
        if (response) {
          this.usersHabilitations = response;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async fetchUserHabilitationDetails(userHabilitationId: string) {
      const feedback = useFeedback();
      const router = useRouter();

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
      try {
        const data = await apiInstance.getUserHabilitationDetails({
          userHabilitationId,
        });

        if (data) {
          this.userHabilitationDetails = data;
          await router.push({
            query: { ...router.currentRoute.value.query, user_habilitation_id: userHabilitationId },
          });
        }
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async batchUpdateUserHabilitations(
      batchUpdateUsersHabilitationsDto: BatchUpdateUsersHabilitationsDto,
    ): Promise<BatchUpdateUserHabilitationsResponse> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
      const data = await apiInstance
        .batchUpdateUserHabilitations({
          batchUpdateUsersHabilitationsDto,
        })
        .catch(async (error) => {
          const errorResponse = await error.response.json();
          throw new Error(errorResponse.message);
        });

      return data;
    },

    async updateSessionHabilitation(
      updateSessionHabilitationDto: UpdateSessionHabilitationDto,
    ): Promise<UpdateSessionHabilitationsResponse> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
      const data = await apiInstance
        .updateSessionHabilitations({
          updateSessionHabilitationDto,
        })
        .catch(async (error) => {
          const errorResponse = await error.response.json();
          throw new Error(errorResponse.message);
        });

      return data;
    },

    async updateBookingHabilitation(
      updateBookingHabilitationDto: UpdateBookingHabilitationDto,
    ): Promise<UpdateBookingHabilitationResponse> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
      const data = await apiInstance.updateBookingHabilitation({
        updateBookingHabilitationDto,
      });

      return data;
    },

    async fetchAllUserHabilitations(userId: string) {
      const feedback = useFeedback();
      const requestId = ++latestHabilitationsRequestId;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, UserDetailApi);
        // lazyLoad:false = eager load, returns every row (lazyLoad:true is the paginated
        // typeahead mode); the full list is required so the "already exists" check is reliable.
        const response = await apiInstance.getUserDetailHabilitations({ userId, lazyLoad: false });
        // Drop the response if a newer selection has already superseded this request.
        if (requestId !== latestHabilitationsRequestId) return;
        this.userHabilitationsList = response?.data ?? [];
      } catch {
        if (requestId !== latestHabilitationsRequestId) return;
        feedback.error(`erreur`, "small");
        this.userHabilitationsList = [];
      }
    },
    clearUserHabilitationsList() {
      // Bump the id so any in-flight fetch can no longer overwrite the cleared list.
      latestHabilitationsRequestId++;
      this.userHabilitationsList = null;
    },

    async createUpdate(body: { name: string; durationYears: number }) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
        const data = await apiInstance.createOrUpdateHabilitation({
          createHabilitationDto: {
            habilitationId: null,
            name: body.name,
            durationYears: body.durationYears,
          },
        });
        return data;
      } catch {
        feedback.error(`Erreur`, "small");
      }
    },

    async uploadCertificate(userHabilitationId: string, file: any) {
      const feedback = useFeedback();
      try {
        file.progress = 0;
        file.status = null;

        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);

        file.progress = 25;

        const result = await apiInstance.uploadCertificate({
          userHabilitationId,
          file,
        });

        file.progress = 100;
        file.status = true;

        return result;
      } catch {
        file.progress = 100;
        file.status = false;
        feedback.error(`Erreur`, "small");
      }
    },

    async removeCertificate(userHabilitationId: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);

        const result = await apiInstance.removeCertificate({
          userHabilitationId,
        });

        return result;
      } catch {
        feedback.error(`Erreur`, "small");
      }
    },

    updateRow<K extends keyof UsersHabilitationsResponseModel>(
      userHabilitationId: string,
      key: K,
      value: UsersHabilitationsResponseModel[K],
    ) {
      const rowIndex: number =
        this.usersHabilitations?.data?.findIndex((el) => el.userHabilitationId === userHabilitationId) ?? -1;
      if (this.usersHabilitations?.data && key && value && rowIndex !== -1) {
        const row: UsersHabilitationsResponseModel = this.usersHabilitations.data[rowIndex];
        row[key] = value;
      }
    },

    async fetchCertificateUrl(userHabilitationId: string): Promise<UserHabilitationCertificateModel | null> {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
        return await apiInstance.getCertificateUrl({ userHabilitationId });
      } catch {
        // 422 when no certificate is uploaded; call sites surface the error feedback
        return null;
      }
    },

    cleanValidationError() {
      this.userError = null;
      this.habilitationError = null;
      this.durationYearsError = null;
    },

    async validateUserHabilitationCreation() {
      const userHabilitationsValidation = useUsersHabilitationsValidation();
      const t$ = await userHabilitationsValidation.validateUsersHabilitationsCreation();
      if (t$.value.$errors) {
        this.userError = t$.value.userId.$silentErrors.map((m: any) => m.$message).join("");
        this.habilitationError = t$.value.habilitationName.$silentErrors.map((m: any) => m.$message).join("");
        this.durationYearsError = t$.value.durationYears.$silentErrors.map((m: any) => m.$message).join("");
      }
    },

    async validateUserHabilitationUpdate() {
      const userHabilitationsValidation = useUsersHabilitationsValidation();
      const t$ = await userHabilitationsValidation.validateUsersHabilitationsUpdate();
      if (t$.value.$errors) {
        this.userError = t$.value.userId.$silentErrors.map((m: any) => m.$message).join("");
        this.habilitationError = t$.value.habilitationName.$silentErrors.map((m: any) => m.$message).join("");
        this.durationYearsError = t$.value.durationYears.$silentErrors.map((m: any) => m.$message).join("");
      }
    },

    async exportUserHabilitationsCSV() {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
        return await apiInstance.exportUserHabilitationsCSV();
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async exportUserHabilitationsXLSX(): Promise<Blob | undefined> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
        return await apiInstance.exportUserHabilitationsXLSX();
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },

    async fetchNotExcludedUserHabilitation(userIdList: string[]) {
      const feedback = useFeedback();
      const filters = useFilters();
      const pagination = usePagination();

      try {
        const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
        const filtersQuery = filters.getFiltersForSDK();

        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);
        const result = await apiInstance.getUsersHabilitationsNotExcluded({
          filters: filtersQuery.filters,
          usersList: userIdList,
          ...paginateOptions,
        });

        if (result.length) {
          this.selectedUsersIdList = result.map((user) => ({
            userId: user.userId,
            email: user.email,
            firstname: user.firstname ?? "",
            status: user.userStatus,
            lastname: user.lastname ?? "",
            employeeNumber: user.userEmployeeNumber ?? "",
            displayValue: user.userDisplayValue,
            userDisplayValue: user.userDisplayValue,
          }));
        } else {
          this.selectedUsersIdList = [];
        }
      } catch {
        feedback.error(`erreur`, "small");
        this.selectedUsersIdList = [];
      }
    },

    /**
     * @do reset data and paginate from usepaginate
     * @reset offset and maxOffset similar to useEmployeeTrainingsStore resetPaginate()
     */
    resetPaginate() {
      const paginate = usePagination();
      this.habilitationEmployee = [];
      this.offset = 0;
      this.maxOffset = 0;
      paginate.paginateOptions.value.offset = this.offset;
    },
    /**
     * @do fetch habilitation employee
     * @param noFilter if true, no filter will be applied
     * @note This API returns all habilitations at once (no server-side pagination)
     */
    async fetchHabilitationEmployee(noFilter?: boolean) {
      // Prevent duplicate fetches while loading
      if (this.habilitationLoading) {
        return;
      }

      this.habilitationLoading = true;

      try {
        const pagination = usePagination();
        const search = !noFilter ? pagination.paginateOptions.value.search : "";
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, HabilitationsApi);

        const result = await apiInstance.getMyUserHabilitations({
          search: search as string,
        });

        if (result) {
          this.habilitationEmployee = result;
          // Since API returns all items at once, set offset = maxOffset to prevent further scroll fetches
          this.offset = result.length;
          this.maxOffset = result.length;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.habilitationLoading = false;
      }
    },
  },
});
